import { type PixelCanvas, sprite } from '../../../lib';
import { burst, frames3, frames4, muzzle, paint, shot, type Tone } from './shared';

/**
 * The Gloop Gun's shots (the Ooze set's gun, ADR-0160 amended): a blob of goo (turned along its flight, wobbling as it
 * flies, a tail of goo stretched behind it), a splurt of goo at the muzzle, a splat where it lands that drips; and the
 * flourish's puddle (`ooze_puddle`: the body melting into a pool that wobbles flat) and a drop (`ooze_drop`).
 */

/** Goo at (x, y) of a round lump about (cx, cy): pink on its top-left, plum on its bottom-right, a white gleam. */
const gooAt = (x: number, y: number, cx: number, cy: number, rx: number, ry: number, gleam = true): Tone => {
  const [nx, ny] = [(x - cx) / rx, (y - cy) / ry];
  const d = nx * nx + ny * ny;
  if (d > 1) return null;
  if (gleam && Math.hypot(nx + 0.35, ny + 0.4) < 0.22) return 'w';
  const l = -nx * 0.7 - ny * 0.7;
  return d > 0.6 && l < -0.1 ? 'P' : l > 0.35 && d > 0.3 ? 'i' : 'p';
};

/** The blob, its head right at (24, 8): wider or longer in turn as it wobbles, a tail of goo stretched behind. */
const blob = (f: number) => (c: PixelCanvas) => {
  const [rx, ry] = (
    [
      [5, 4],
      [5.6, 3.5],
      [5, 4],
      [4.4, 4.4],
    ] as const
  )[f]!;
  paint(c, (x, y) => {
    const head = gooAt(x, y, 23, 8, rx, ry);
    if (head) return head;
    // The tail: narrowing behind it, plum underneath.
    if (x > 12 && x < 23 && Math.abs(y - 8) < (x - 12) * 0.28) return y > 8.6 ? 'P' : 'p';
    if (Math.hypot(x - 10.5 + (f % 2), y - 8) < 1) return 'p';
    return null;
  });
};

/** The splurt at the muzzle: a lump of goo bursting out, then a spray of drops flying on, fewer and further. */
const splurt = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    if (f === 0) {
      const lump = gooAt(x, y, 10, 20, 7, 6);
      if (lump) return lump;
    }
    if (f === 1 && gooAt(x, y, 14, 20, 5, 4, false)) return gooAt(x, y, 14, 20, 5, 4, false);
    const n = [5, 6, 4][f] as number;
    const reach = [12, 20, 28][f] as number;
    for (let i = 0; i < n; i++) {
      const a = (i - (n - 1) / 2) * 0.32;
      const r = reach * (0.8 + ((i * 5) % 3) * 0.12);
      const s = [2.6, 2.1, 1.6][f] as number;
      const drop = gooAt(x, y, 6 + Math.cos(a) * r, 20 + Math.sin(a) * r, s, s, false);
      if (drop) return drop;
    }
    return null;
  });

/** Where it lands: a blob flattening into a splat, arms flung out, drips hanging under it, then drops and a stain. */
const splat = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const [dx, dy] = [x - 22, y - 22];
    const a = Math.atan2(dy, dx);
    const d = Math.hypot(dx, dy * 1.2);
    if (f === 0) return gooAt(x, y, 22, 22, 7, 5.5);
    // The splat: a rounded star of goo, its arms reaching further each frame.
    const arms = 1 + 0.45 * Math.max(0, Math.sin(a * 6 + 0.5)) ** 2;
    const R = ([0, 8, 9, 7] as const)[f]! * arms;
    if (f < 3 && d < R) {
      if (d > R - 1.2) return dx + dy > 0 ? 'P' : 'i';
      return Math.hypot(dx + 3, dy + 3) < 1.5 ? 'w' : 'p';
    }
    if (f === 3 && d < R) return d > R - 1.2 ? 'P' : 'p';
    // Drops flung out past it, and drips hanging off its foot.
    const out = ([0, 13, 17, 20] as const)[f] as number;
    for (let i = 0; i < 6; i++) {
      const b = (i / 6) * Math.PI * 2 + 0.3;
      if (gooAt(x, y, 22 + Math.cos(b) * out, 22 + Math.sin(b) * out * 0.8, 1.6, 1.6, false)) return 'p';
    }
    for (const [ux, len] of [
      [-4, 4 + f * 2],
      [3, 2 + f * 3],
    ] as const) {
      const top = 22 + 6;
      if (Math.abs(x - 22 - ux) < 1 && y > top - 2 && y < top + len) return 'p';
      if (Math.hypot(x - 22 - ux, y - top - len) < 1.5) return 'P';
    }
    return null;
  });

/**
 * The puddle (32 × 20, its middle at (16, 14)): the body slumping into a mound of goo, spreading flat, then wobbling,
 * its rim rippling, its eyes still floating in it; 8 frames.
 */
const PUDDLE = [
  [6, 9],
  [8, 7],
  [10, 5],
  [12, 4],
  [13, 3],
  [12, 3.5],
  [13, 3],
  [12.5, 3.2],
] as const;
const puddle = (f: number) => (c: PixelCanvas) => {
  const [rx, h] = PUDDLE[f]!;
  paint(c, (x, y) => {
    const dx = (x - 16) / rx;
    if (Math.abs(dx) > 1) return null;
    // A mound: its top a dome of height h over its base, its base an ellipse on the ground.
    const base = 14 + Math.sqrt(1 - dx * dx) * 3;
    const top = 14 - Math.sqrt(1 - dx * dx) * h + Math.sin(x * 0.9 + f) * 0.5;
    if (y < top || y > base) return null;
    // The eyes, floating.
    for (const ex of [13, 18]) if (f < 7 && Math.hypot(x - ex, y - (top + 2)) < 1.3) return x > ex ? 'k' : 'w';
    if (y < top + 1) return 'i';
    if (y > base - 1) return 'P';
    if (Math.hypot(x - 11, y - top - 1.8) < 0.9) return 'w';
    return x > 16 + rx * 0.5 ? 'P' : 'p';
  });
};

/** A drop: round, lit on its top-left; it stretches as it flies. */
const drop = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => gooAt(x, y, 4, 4, f === 0 ? 2.2 : 1.8, f === 0 ? 2.2 : 2.8, false));

export default [
  sprite({
    name: 'gloop_shot',
    ...shot,
    notes: `The Gloop Gun's bullet (ADR-0160 amended): a blob of magenta goo, its head on the pellet's head (lit pink on its
top-left, plum underneath, a white gleam), a tail of goo stretched behind it, a drop trailing; it wobbles as it flies (wider,
longer, rounder over 4 frames). Turned along its flight. Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { idle: { fps: 12, frames: frames4(blob) } },
  }),
  sprite({
    name: 'gloop_muzzle',
    ...muzzle,
    notes: `The Gloop Gun's muzzle flash (ADR-0160 amended): a splurt of goo, a lump bursting out of the muzzle with drops in a
fan, then the lump flying on and the drops further, smaller and fewer. Points right. Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(splurt) } },
  }),
  sprite({
    name: 'gloop_burst',
    ...burst,
    notes: `The Gloop Gun's hit (ADR-0160 amended): the blob flattening into a splat, a rounded star of goo flinging its arms
out, drops flying past it, two drips running down off its foot and lengthening, then a stain. 4 frames. Rubric (4×): S4 R4
P5 L4 C4 A4 F4.`,
    anims: { burst: { fps: 18, loop: false, frames: frames4(splat) } },
  }),
  sprite({
    name: 'ooze_puddle',
    group: 'fx',
    size: [32, 20],
    origin: [16, 14],
    density: 4,
    notes: `The Ooze's knock-out puddle (ADR-0160 amended): the body slumping into a mound of magenta goo that spreads flat and
wobbles, its rim rippling, two eyes floating in it until the last frame. 8 frames. Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { melt: { fps: 10, loop: false, frames: [0, 1, 2, 3, 4, 5, 6, 7].map(puddle) } },
  }),
  sprite({
    name: 'ooze_drop',
    group: 'fx',
    size: [8, 8],
    origin: [4, 4],
    density: 4,
    notes: `The Ooze's knock-out drop (ADR-0160 amended): a drop of goo, round, then stretched. Rubric (4×): S4 R4 P5 L4 C4 A4
F4.`,
    anims: { idle: { fps: 10, frames: [drop(0), drop(1)] } },
  }),
];
